import type { ReactNode } from 'react';
import type { Recipient, Status } from './OutreachDialog.tsx';

import { faBell } from '@fortawesome/pro-regular-svg-icons';

import {
  Content,
  DeliverySummary,
  Footer,
  Header,
  MessagePreview,
  RecipientDetails,
  RecipientTableSection,
  Root,
} from './OutreachDialog.tsx';

interface CommonProps {
  onClose: () => void;
  onConfirm: () => void;
  // 'error' keeps the dialog open (dismiss still works) but blocks confirming — e.g. the
  // single-send preview failed, so there is no vetted copy to send.
  status: Status;
  title: string;
  // ReactNode (not string) so the single-send flow can show a skeleton while Mia's real copy
  // is being generated server-side.
  message: ReactNode;
  // The channel(s) the send goes out on, rendered after "Sent via". ReactNode so the single-send
  // flow can show an inline skeleton until the preview reveals the resolved channel.
  channelLabel: ReactNode;
  confirmLabel: string;
}

interface BulkRequestConfirmationDialogProps extends CommonProps {
  children: ReactNode;
  isBulk: true;
  recipient?: never;
  recipientCount: number;
}

interface SingleRequestConfirmationDialogProps extends CommonProps {
  children?: never;
  isBulk?: false;
  recipient: Recipient;
  recipientCount?: never;
}

type RequestConfirmationDialogProps =
  | BulkRequestConfirmationDialogProps
  | SingleRequestConfirmationDialogProps;

export function RequestConfirmationDialog(props: RequestConfirmationDialogProps) {
  if (props.isBulk) {
    return (
      <Root onClose={props.onClose} maxWidth="lg">
        <Header color="error" icon={faBell}>
          {props.title}
        </Header>

        <Content>
          <MessagePreview label="Message Template">{props.message}</MessagePreview>

          <RecipientTableSection note="Bracketed details will be personalized for each recipient">
            {props.children}
          </RecipientTableSection>

          <DeliverySummary>
            Sent via {props.channelLabel} · {props.recipientCount} patients will be prompted to
            confirm
          </DeliverySummary>
        </Content>

        <Footer
          color="error"
          confirmLabel={props.confirmLabel}
          icon={faBell}
          onConfirm={props.onConfirm}
          status={props.status}
        />
      </Root>
    );
  }

  return (
    <Root onClose={props.onClose}>
      <Header color="error" icon={faBell}>
        {props.title}
      </Header>

      <Content>
        <RecipientDetails recipient={props.recipient} />

        <MessagePreview label="Message">{props.message}</MessagePreview>

        <DeliverySummary>
          Sent via {props.channelLabel} · Patient will be prompted to confirm
        </DeliverySummary>
      </Content>

      <Footer
        color="error"
        confirmLabel={props.confirmLabel}
        icon={faBell}
        onConfirm={props.onConfirm}
        status={props.status}
      />
    </Root>
  );
}
